import {
    collection,
    doc,
    addDoc,
    getDocs,
    query,
    where,
    writeBatch,
    updateDoc,
    deleteDoc,
} from 'firebase/firestore'
import type { DocumentTransactionAssociation } from '~/storage/types'
import { StorageModuleDeps } from '~common/storage/types'
import { collectionRef, fillDoc } from '~common/utils/firestore-helpers'

export class MatchingAssociationsStorageModule {
    constructor(private readonly deps: StorageModuleDeps) {}

    async storeAssociations(
        associations: Omit<
            DocumentTransactionAssociation,
            'id' | 'createdAt' | 'updatedAt'
        >[],
        now: number = Date.now(),
    ): Promise<DocumentTransactionAssociation[]> {
        const userId = this.deps.ensureAuthenticated()
        const col = collectionRef<DocumentTransactionAssociation>(
            this.deps.db,
            'users',
            userId,
            'documentTransactionAssociations',
        )
        const storedAssociations: DocumentTransactionAssociation[] = []
        const batch = writeBatch(this.deps.db)
        associations.forEach((assoc) => {
            const docRef = doc(col)
            const toStore: DocumentTransactionAssociation = {
                id: docRef.id,
                ...assoc,
                createdAt: now,
                updatedAt: now,
            }
            storedAssociations.push(toStore)
            batch.set(docRef, toStore)
        })
        await batch.commit()
        return storedAssociations
    }

    async storeAssociation(
        assoc: Omit<
            DocumentTransactionAssociation,
            'id' | 'createdAt' | 'updatedAt'
        >,
        now: number = Date.now(),
    ): Promise<DocumentTransactionAssociation> {
        const userId = this.deps.ensureAuthenticated()
        const col = collection(
            this.deps.db,
            'users',
            userId,
            'documentTransactionAssociations',
        )
        const docRef = await addDoc(col, {
            ...assoc,
            createdAt: now,
            updatedAt: now,
        })
        const newAssoc: DocumentTransactionAssociation = {
            id: docRef.id,
            ...assoc,
            createdAt: now,
            updatedAt: now,
        }
        return newAssoc
    }

    async getAssociationsForTransaction(
        transactionId: string,
    ): Promise<DocumentTransactionAssociation[]> {
        const userId = this.deps.ensureAuthenticated()
        const col = collectionRef<DocumentTransactionAssociation>(
            this.deps.db,
            'users',
            userId,
            'documentTransactionAssociations',
        )
        const q = query(col, where('transactionId', '==', transactionId))
        const snapshot = await getDocs(q)
        return snapshot.docs.map(fillDoc)
    }

    async getAssociationsForDocument(
        documentId: string,
    ): Promise<DocumentTransactionAssociation[]> {
        const userId = this.deps.ensureAuthenticated()
        const col = collectionRef<DocumentTransactionAssociation>(
            this.deps.db,
            'users',
            userId,
            'documentTransactionAssociations',
        )
        const q = query(col, where('documentId', '==', documentId))
        const snapshot = await getDocs(q)
        return snapshot.docs.map(fillDoc)
    }

    async getAssociationsForTransactionAndDocument(
        transactionId: string,
        documentId: string,
    ): Promise<DocumentTransactionAssociation> {
        const userId = this.deps.ensureAuthenticated()
        const col = collectionRef<DocumentTransactionAssociation>(
            this.deps.db,
            'users',
            userId,
            'documentTransactionAssociations',
        )
        const q = query(
            col,
            where('transactionId', '==', transactionId),
            where('documentId', '==', documentId),
        )
        const snapshot = await getDocs(q)
        return snapshot.docs.map(fillDoc)[0]
    }

    async getAllAssociations(): Promise<DocumentTransactionAssociation[]> {
        const userId = this.deps.ensureAuthenticated()
        const col = collectionRef<DocumentTransactionAssociation>(
            this.deps.db,
            'users',
            userId,
            'documentTransactionAssociations',
        )
        const snapshot = await getDocs(col)
        return snapshot.docs.map(fillDoc)
    }

    async clearForTransaction(transactionId: string): Promise<void> {
        const userId = this.deps.ensureAuthenticated()
        const q = query(
            collection(
                this.deps.db,
                'users',
                userId,
                'documentTransactionAssociations',
            ),
            where('transactionId', '==', transactionId),
        )
        const snapshot = await getDocs(q)
        const batch = writeBatch(this.deps.db)
        snapshot.docs.forEach((d) => batch.delete(d.ref))
        await batch.commit()
    }

    async clearForDocument(documentId: string): Promise<void> {
        const userId = this.deps.ensureAuthenticated()
        const q = query(
            collection(
                this.deps.db,
                'users',
                userId,
                'documentTransactionAssociations',
            ),
            where('documentId', '==', documentId),
        )
        const snapshot = await getDocs(q)
        const batch = writeBatch(this.deps.db)
        snapshot.docs.forEach((d) => batch.delete(d.ref))
        await batch.commit()
    }

    async updateAssociation(
        assocId: string,
        updates: Partial<
            Omit<
                DocumentTransactionAssociation,
                'id' | 'createdAt' | 'updatedAt'
            >
        >,
        now: number = Date.now(),
    ): Promise<void> {
        const userId = this.deps.ensureAuthenticated()
        const docRef = doc(
            this.deps.db,
            'users',
            userId,
            'documentTransactionAssociations',
            assocId,
        )
        await updateDoc(docRef, { ...updates, updatedAt: now })
    }

    /**
     * Deletes a specific association by its ID.
     */
    async deleteAssociation(assocId: string): Promise<void> {
        const userId = this.deps.ensureAuthenticated()
        const docRef = doc(
            this.deps.db,
            'users',
            userId,
            'documentTransactionAssociations',
            assocId,
        )
        await deleteDoc(docRef)
    }
}
